import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PropertiesStore } from '@hortplus/properties-react';
import { Formik } from 'formik';
import { observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import LoadingOverlay from '../LoadingOverlay';
import { errorToast } from '../helpers/ToastUtils';
import { useStores } from '../stores/useStores';
import { dateFormatRegex } from '../utils';
import TrapMonitoringRecordsForm from './TrapMonitoringRecordsForm';

function AddTrapMonitoringRecordsModal(props) {
  const [show, setShow] = useState(false);
  const { traplogStore } = useStores();

  const databaseYear = traplogStore.selectedDatabaseYear() - 1;

  const hide = () => {
    setShow(false);
  };

  // Use the selected trapping zone if one selected.
  // Otherwise, get trapping zone from props.
  const trappingZone = traplogStore.selectedTrappingZoneResults
    ? traplogStore.selectedTrappingZoneResults
    : traplogStore.trappingResults[props.trappingZone?.id];
  const trappingZoneResults = trappingZone.results
    .slice()
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const monitoringDates = trappingZone?.results
    .filter((result) => result.trapping_zone_sprays.length === 0)
    .map((result) => result.date)
    .sort((a, b) => new Date(a) - new Date(b));

  const defaultDate = () => {
    if (!monitoringDates || monitoringDates.length === 0) return '';
    const tzHasBiofix = trappingZoneResults[
      trappingZoneResults.length - 1
    ]?.trapping_zone_sprays?.some((tzs) => tzs.biofix);
    const days = tzHasBiofix ? 14 : 7;
    // Return the last monitoring date plus 'days'.
    const now = new Date(monitoringDates[monitoringDates.length - 1]);
    now.setDate(now.getDate() + days);
    const date = now.toISOString().split('T')[0];
    return date;
  };

  return (
    <Fragment>
      <Button
        className='mx-2'
        variant='outline-primary'
        onClick={() => setShow(true)}
      >
        <FontAwesomeIcon icon={faPlus} /> Add Monitoring
      </Button>
      <Modal backdrop='static' show={show} onHide={() => hide()}>
        <Modal.Header closeButton>
          <Modal.Title>Add Monitoring</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={{
              visited_date: defaultDate(),
              operator: '',
              trapMonitoringRecords: props.trappingZone.traps
                .filter((trap) =>
                  trap.trap_lure.insects.some(
                    (insect) =>
                      insect.insect_group.id ===
                      traplogStore.selectedInsectGroupId
                  )
                )
                .map((trap) => {
                  return {
                    trap: trap,
                    trap_id: trap.id,
                    amount: 0,
                    lure_changed: false,
                    base_changed: false,
                    comments: '',
                    visited_at: '',
                    location: '',
                    location_accuracy: ''
                  };
                })
            }}
            validate={(values, props) => {
              const errors = {};
              const visitedYear = moment(values.visited_date).year();

              if (!values.visited_date) errors.visited_date = 'Required';
              else if (visitedYear < databaseYear) {
                errors.visited_date = `The year must be ${databaseYear} or later.`;
              } else if (visitedYear > databaseYear + 1) {
                errors.visited_date = `The year cannot be larger than ${
                  databaseYear + 1
                }.`;
              } else if (!dateFormatRegex.test(values.visited_date)) {
                errors.visited_date =
                  'Invalid date. Please use the format "YYYY-MM-DD", and be careful of leap years.';
              } else {
                // Validate the 'visited_date' date range.
                const visitedDate = new Date(values.visited_date);
                const month = visitedDate.getMonth() + 1;
                if (month < 9 && month > 5) {
                  errors.visited_date =
                    'Date must be between September and the following May';
                }
              }
              if (!values.operator) errors.operator = 'Required';

              const trapMonitoringRecords = {};
              values.trapMonitoringRecords.forEach((record) => {
                const recordErrors = {};

                if (record.amount !== 0 && !record.amount)
                  recordErrors.amount = 'Required';

                if (Object.keys(recordErrors).length > 0)
                  trapMonitoringRecords[record.trap.id] = recordErrors;
              });

              if (Object.keys(trapMonitoringRecords).length > 0)
                errors.trapMonitoringRecords = trapMonitoringRecords;

              return errors;
            }}
            onSubmit={(values, actions) => {
              const body = {
                records: values.trapMonitoringRecords.map((record) => {
                  return {
                    ...record,
                    visited_date: values.visited_date,
                    operator: values.operator
                  };
                })
              };

              fetch(
                `${process.env.REACT_APP_TRAPLOG_API_URL}api/trap-monitoring-records`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: `Bearer ${traplogStore.bearerToken}`,
                    SeasonalDatabase: traplogStore.selectedDatabase
                  },
                  body: JSON.stringify(body)
                }
              )
                .then(async (res) => {
                  if (res.ok) return res.json();
                  else if (res.status === 401)
                    traplogStore.refreshBearerToken();
                  else if (res.status === 409) {
                    const date = moment(values.visited_date).format(
                      'DD/MM/YYYY'
                    );
                    errorToast(
                      <div>A monitoring record already exists with date <b>{date}.</b></div>
                    );
                  } else throw Error(res.statusText);
                })
                .then((data) => {
                  if (data) {
                    traplogStore.loadTrappingZones(
                      traplogStore.bearerToken,
                      traplogStore.refreshBearerToken,
                      traplogStore.selectedDatabase,
                      props.propertiesStore.selectedPropertyId
                    );
                    hide();
                  }
                })
                .catch((err) => {
                  if (process.env.NODE_ENV === 'development')
                    console.error(err);
                })
                .finally(() => {
                  actions.setSubmitting(false);
                });
            }}
          >
            {(props) => (
              <Fragment>
                <TrapMonitoringRecordsForm {...props} />
                <Button
                  varient='primary'
                  type='button'
                  onClick={props.handleSubmit}
                >
                  Submit
                </Button>
                {props.isSubmitting ? <LoadingOverlay /> : null}
              </Fragment>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </Fragment>
  );
}

AddTrapMonitoringRecordsModal.propTypes = {
  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired,
  trappingZone: PropTypes.object.isRequired
};

export default observer(AddTrapMonitoringRecordsModal);
